import { useEffect, useState, useCallback } from "react";
import { MessageCircle, Mail, ShoppingBasket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/common";
import { api, errMsg, fmtDateTime, fmtQty, waLink } from "@/lib/api";
import { toast } from "sonner";

function SupplierOrder({ g, store, onSent }) {
  const [qty, setQty] = useState(() => Object.fromEntries(g.items.map((i) => [i.id, i.suggested])));
  const [notes, setNotes] = useState("");
  const s = g.supplier;
  const items = g.items.filter((i) => Number(qty[i.id]) > 0).map((i) => ({ name: i.name, unit: i.unit, qty: Number(qty[i.id]) }));
  const text = [`*Pedido de compra · ${store}*`, s ? `Fornecedor: ${s.name}` : "", "", ...items.map((i) => `• ${i.name}: ${fmtQty(i.qty)} ${i.unit}`), notes ? `\nObs.: ${notes}` : "", "", "Obrigado!"].filter((x, k) => x || k > 1).join("\n");
  const save = async (send_email) => {
    try {
      const { data } = await api.post("/stock/purchase-orders", { supplier_id: s?.id || null, items, notes, send_email });
      toast.success(send_email ? `Pedido nº ${data.number} enviado para ${data.emailed_to}` : `Pedido nº ${data.number} registrado`);
      onSent();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const key = s?.id || "none";
  return (
    <div className="card-surface p-4 space-y-3" data-testid={`po-card-${key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-display font-semibold">{s ? s.name : "Sem fornecedor definido"}</p>
        <span className="text-xs text-muted-foreground">{[s?.phone, s?.email].filter(Boolean).join(" · ") || "Defina o fornecedor no cadastro do insumo"}</span>
      </div>
      <ul className="divide-y text-sm">
        {g.items.map((i) => (
          <li key={i.id} className="flex items-center gap-3 py-2">
            <span className="flex-1 min-w-0">{i.name}<br /><span className="text-xs text-muted-foreground">Saldo {fmtQty(i.stock)} · mínimo {fmtQty(i.min_stock)} {i.unit}</span></span>
            <Input type="number" step="0.001" min="0" value={qty[i.id]} onChange={(e) => setQty((q) => ({ ...q, [i.id]: e.target.value }))} className="h-9 w-24" data-testid={`po-qty-${i.id}`} />
            <span className="w-8 text-xs text-muted-foreground">{i.unit}</span>
          </li>
        ))}
      </ul>
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observações (prazo, forma de entrega...)" data-testid={`po-notes-${key}`} />
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" disabled={!items.length} onClick={() => save(false)} data-testid={`po-whatsapp-${key}`}>
          <a href={waLink(s?.phone || "", text)} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4 mr-1 text-emerald-500" />Enviar por WhatsApp</a>
        </Button>
        <Button disabled={!items.length || !s?.email} onClick={() => save(true)} data-testid={`po-email-${key}`}><Mail className="h-4 w-4 mr-1" />Enviar por e-mail</Button>
      </div>
    </div>
  );
}

export function PurchaseOrders({ store }) {
  const [groups, setGroups] = useState(null);
  const [history, setHistory] = useState([]);
  const load = useCallback(() => Promise.all([api.get("/stock/purchase-suggestions"), api.get("/stock/purchase-orders")]).then(([g, h]) => { setGroups(g.data); setHistory(h.data); }), []);
  useEffect(() => { load(); }, [load]);
  if (!groups) return null;
  return (
    <div className="space-y-4">
      {!groups.length ? <EmptyState icon={ShoppingBasket} title="Nada para comprar" text="Nenhum item está abaixo do estoque mínimo." /> : (
        <div className="grid gap-4 xl:grid-cols-2">{groups.map((g) => <SupplierOrder key={g.supplier?.id || "none"} g={g} store={store} onSent={load} />)}</div>
      )}
      <div className="card-surface">
        <p className="eyebrow border-b p-4">Pedidos enviados</p>
        <ul className="divide-y text-sm" data-testid="po-history">
          {history.map((p) => <li key={p.id} className="flex flex-wrap justify-between gap-2 p-4"><span>Nº {p.number} · {p.supplier_name} · {fmtDateTime(p.created_at)} · {p.user}</span><span className="text-muted-foreground">{p.items.length} itens{p.emailed_to ? ` · e-mail para ${p.emailed_to}` : " · WhatsApp"}</span></li>)}
          {!history.length && <li className="p-4 text-muted-foreground">Nenhum pedido enviado.</li>}
        </ul>
      </div>
    </div>
  );
}
